import { IconBell } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from 'constants/routePaths';
import { notificationTypeLabel } from 'constants/notificationTypes';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { notificationService } from 'services/notificationService';
import { initPushNotifications, onPushNotification } from 'services/pushNotifications';
import socket from 'services/socket';
import { ensureNotifyPermission, notifyDesktop } from 'util/browserNotify';

// Only the unread COUNT is polled (cheap); the list is fetched when the panel
// opens. Mirrors the reference app's 30-second cadence.
const POLL_INTERVAL_MS = 30000;

// Priority — not type — drives the leading dot colour.
const PRIORITY_DOT = {
  urgent: 'bg-danger',
  high: 'bg-warning',
  normal: 'bg-silver',
  low: 'bg-silver',
};

// Relative time with no external library: "just now" / "Xm ago" / "Xh ago" / "Xd ago".
const formatRelative = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

/**
 * Header bell: polls the unread count every 30s and shows the latest 10
 * notifications in a dropdown. Clicking a row marks it read and follows its
 * `link_url`; "See all" opens the full Notifications page.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useOnClickOutside(ref, () => setOpen(false));

  const refreshCount = useCallback(async () => {
    try {
      const res = await notificationService.unreadCount();
      setUnread(res?.data?.unread_count || 0);
    } catch {
      /* best-effort; keep the previous count on error */
    }
  }, []);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationService.list({ limit: 10 });
      setItems(Array.isArray(res?.data) ? res.data : []);
    } catch {
      // A role without `notifications:read` (or a transient error) simply shows
      // an empty panel — never a crash.
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refreshCount]);

  // Refresh unread count (and open list) when a foreground push arrives.
  useEffect(() => {
    return onPushNotification(() => {
      refreshCount();
      if (open) refreshList();
    });
  }, [open, refreshCount, refreshList]);

  // Instant update over the WebSocket: a `notification.new` event bumps the
  // count (and refreshes the open list) with no poll/FCM delay. Complements the
  // 30s poll, which remains the fallback when the socket is down.
  //
  // It also SURFACES the notification (desktop notification, toast fallback) —
  // without this the event only bumped a counter, so anything that isn't a chat
  // message landed silently in the list and was never announced. `tag` is the
  // notification id so an FCM push for the same row collapses instead of
  // double-showing.
  useEffect(() => {
    return socket.on('notification.new', (n) => {
      refreshCount();
      if (open) refreshList();
      if (!n?.title) return;
      notifyDesktop(n.title, {
        body: n.body || '',
        tag: n.id ? String(n.id) : undefined,
        linkUrl: n.link_url || undefined,
        onClick: n.link_url ? () => navigate(n.link_url) : undefined,
      });
    });
  }, [open, refreshCount, refreshList, navigate]);

  // Notifications cleared server-side (e.g. opening a chat marks its messages
  // read) push a `notification.read` event — refresh so the badge drops at once.
  useEffect(() => {
    return socket.on('notification.read', () => {
      refreshCount();
      if (open) refreshList();
    });
  }, [open, refreshCount, refreshList]);

  // The bell only mounts for a signed-in user, so this is the right moment to
  // register the browser for FCM web-push (best-effort; no-ops without a VAPID key).
  // Same moment is right for the desktop-notification permission prompt — the
  // bell is the surface that uses it for every non-chat notification.
  useEffect(() => {
    initPushNotifications();
    ensureNotifyPermission();
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await refreshList();
  };

  const onItemClick = async (n) => {
    if (!n.is_read) {
      try {
        await notificationService.markRead(n.id);
      } catch {
        /* optimistic — ignore a failed mark-read */
      }
      setUnread((u) => Math.max(0, u - 1));
      setItems((list) => list.map((it) => (it.id === n.id ? { ...it, is_read: true } : it)));
    }
    setOpen(false);
    if (n.link_url) navigate(n.link_url);
  };

  const onMarkAll = async () => {
    try {
      await notificationService.markAllRead();
    } catch {
      return;
    }
    setUnread(0);
    setItems((list) => list.map((it) => ({ ...it, is_read: true })));
  };

  const onSeeAll = () => {
    setOpen(false);
    navigate(ROUTES.NOTIFICATIONS);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-label="Notifications"
        aria-haspopup="menu"
        aria-expanded={open}
        className="relative flex h-[38px] w-[38px] items-center justify-center rounded-full text-slate transition-colors hover:bg-black/[0.06]"
      >
        <IconBell className="h-5 w-5" />
        {unread > 0 && (
          <span
            className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-brand ring-2 ring-paper"
            aria-label={`${unread} unread`}
          />
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-80 overflow-hidden rounded-xl border border-hair bg-surface shadow-lg sm:w-96"
        >
          <div className="flex items-center justify-between border-b border-hair bg-mist px-4 py-3">
            <p className="text-sm font-semibold text-ink">Notifications</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={onMarkAll}
                className="text-xs font-semibold text-brand-dark hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <p className="px-4 py-8 text-center text-sm text-slate">Loading…</p>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
                <IconBell className="h-8 w-8 text-silver" />
                <p className="text-sm text-slate">No notifications yet</p>
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => onItemClick(n)}
                  className={`flex w-full items-start gap-3 border-b border-hair px-4 py-3 text-left transition-colors hover:bg-mist ${
                    !n.is_read ? 'bg-brand/[0.04]' : ''
                  }`}
                >
                  <span
                    className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${
                      PRIORITY_DOT[n.priority] || PRIORITY_DOT.normal
                    }`}
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block truncate text-sm ${
                        !n.is_read ? 'font-semibold text-ink' : 'text-graphite'
                      }`}
                    >
                      {n.title}
                    </span>
                    {n.body && (
                      <span className="mt-0.5 block line-clamp-2 text-xs text-slate">{n.body}</span>
                    )}
                    {n.type && (
                      <span className="mt-1 block text-[10px] font-medium uppercase tracking-wide text-silver">
                        {notificationTypeLabel(n.type)}
                      </span>
                    )}
                    <span className="mt-1 block text-[11px] text-silver">
                      {formatRelative(n.created_at)}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>

          <button
            type="button"
            onClick={onSeeAll}
            className="block w-full border-t border-hair px-4 py-2.5 text-center text-sm font-semibold text-brand-dark transition-colors hover:bg-mist"
          >
            See all notifications
          </button>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
